
import React from "react";
import { Button, Card, Col, Container, CurrencyInput, Row, SelectInput } from "banco-components";
import { FormProvider } from "react-hook-form";
import { BANCO_ENUM_PURPOSE } from "banco/constants/generated/constants";
import { useTermsCalculations } from "banco/hooks/useTermsCalculations/useTermsCalculations";
import { formatMoney } from "banco/util/formatter";
import styles from "./styles/v3LoanCalculator.module.scss";
import { useBrandConfig } from "banco/contexts/BrandConfigContext";
import {
  useLoanCalculatorForm,
  useSliderPercentages,
  RepresentativeExampleFooter,
  RangeSlider
} from "../../shared/LoanCalculatorShared";

export const V3LoanCalculator = () => {
  const {
    form_methods,
    desired_advance,
    tenure,
    loan_calc_constants
  } = useLoanCalculatorForm();

  const { display_direct_booking_rep_apr_note, trading_name } = useBrandConfig();

  const term_calcs = useTermsCalculations(
    window?.static_data?.direct_new_rep_products || [], Number(desired_advance), tenure
  );

  const { desired_advance_pct, tenure_pct } = useSliderPercentages(
    desired_advance,
    tenure,
    loan_calc_constants
  );

  return (
    <Card className={ styles["loan-calculator"] }>
      <FormProvider { ...form_methods }>
        <Card.Body>
          <div className="d-flex flex-column mb-4" style={ { gap: "2rem" } }>
            <div>
              <CurrencyInput
                fieldName="desired_advance"
                formLabel="How much would you like to borrow?"
                additionalClasses={ `mb-4 ${styles["amount-term-inputs"]}` }
                allowDecimal={ false }
              />
              <RangeSlider
                name="desired_advance"
                value={ desired_advance }
                min={ loan_calc_constants.min_loan_value }
                max={ loan_calc_constants.max_loan_value }
                step={ loan_calc_constants.loan_value_step }
                percentage={ desired_advance_pct }
                onChange={ (value) =>
                  form_methods.setValue("desired_advance", value, { shouldDirty: true })
                }
                dataFbInput="desired_advance_slider"
              />
            </div>
            <div>
              <SelectInput
                fieldName="tenure"
                formLabel="For how many months?"
                additionalClasses={ `mb-4 ${styles["amount-term-inputs"]}` }
                optionsList={ loan_calc_constants.loan_tenures }
              />
              <RangeSlider
                name="tenure"
                value={ tenure }
                min={ loan_calc_constants.loan_tenures_min }
                max={ loan_calc_constants.loan_tenures_max }
                step={ loan_calc_constants.loan_tenures_step }
                percentage={ tenure_pct }
                onChange={ (value) =>
                  form_methods.setValue("tenure", value, { shouldDirty: true })
                }
                dataFbInput="tenure_slider"
              />
            </div>
          </div>
          <Container>
            <Row>
              <Col xs={ 12 } lg={ 7 } >
                <SelectInput
                  fieldName="purpose"
                  noLabel
                  optionsList={ BANCO_ENUM_PURPOSE }
                  defaultLabel="Purpose"
                />
              </Col>
              <Col xs={ 12 } lg={ 5 } className="d-flex justify-content-end align-items-start">
                <Button
                  variant="secondary"
                  href="/apply?hide_rep_apr=true"
                  data-fb-button="Apply Button"
                >
                  Get your quote
                </Button>
              </Col>
            </Row>
          </Container>
          <p className="mb-0 mt-4">
            The rate you are offered will depend on your personal circumstances.
            Getting a quote won’t affect your credit score.
          </p>
        </Card.Body>
        <Card>
          <Card.Body>
            <Container>
              <Row className="values-table">
                <Col xs={ 6 } className="mb-3 mb-md-4">
                  <p className="table-label">Monthly repayments</p>
                  <span className="value">
                    { formatMoney(term_calcs?.approximateMonthlyPayment) }
                  </span>
                </Col>
                <Col xs={ 6 } className="mb-3 mb-md-4">
                  <p className="table-label">Interest</p>
                  <span className="value">
                    { formatMoney(term_calcs?.interestAndFees) }
                  </span>
                </Col>
                <Col xs={ 6 }>
                  <p className="table-label">Representative</p>
                  <span className="value">
                    { term_calcs?.product?.apr }% APR
                  </span>
                </Col>
                <Col xs={ 6 }>
                  <p className="table-label">Total to repay</p>
                  <span className="value">
                    { formatMoney(term_calcs?.totalRepayable) }
                  </span>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
        <div
          className={ styles["loan-calculator-card-table"] }
          data-fb-panel="Representative Example">
          <table>
            <tfoot>
              <tr>
                <th className={ styles.th }>Representative example</th>
              </tr>
              <RepresentativeExampleFooter
                loan_calc_constants={ loan_calc_constants }
                display_direct_booking_rep_apr_note={ display_direct_booking_rep_apr_note }
                trading_name={ trading_name }
                tdClassName={ styles.td }
              />
            </tfoot>
          </table>
        </div>
      </FormProvider>
    </Card>
  );
};
